import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useGoogleCalendar } from '../features/book-equipment/calendar-timeline/hooks/useGoogleCalendar';
import { OwcTypography } from '@one/react';
import { Autocomplete, TextField,  Checkbox, Box, Typography, CircularProgress } from '@mui/material';
import { formatTitle } from '../utils/helpers/googleCalendar';
import { GoogleAuth } from './shared/GoogleAuth';
import { isGoogleCalendarConnected } from '../utils/store/googleCalendarPersistence';
import { snackbarService } from '@one/web-components/dist/index.cjs.js';
import { SNACKBAR_DURATION } from '../components/EquipmentList/Constants/constants';


const NUMBER_OF_CALENDAR_TO_SELECT = 5;

const GoogleSyncContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
`;


const GoogleCalendarSync = () => {
  const {
    calendars,
    calendarData,
    error,
    toggleCalendar,
    setError,
    refreshAccessToken,
    loadGoogleCalendars,
    isLoading,
    // eslint-disable-next-line    
    signOut,
  } = useGoogleCalendar();

  const [hasTokensInStorage, setHasTokensInStorage] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  // Use refs to avoid stale closures in interval/event handlers
  const hasAttemptedLoadRef = useRef(false);
  const authErrorOccurredRef = useRef(false);

  useEffect(() => {
    const initializeGoogleCalendar = async () => {
      setIsInitializing(true);
      hasAttemptedLoadRef.current = true;
      
      const tokensExist = isGoogleCalendarConnected();
      setHasTokensInStorage(tokensExist);
      
      // Skip initialization if we already have calendars loaded
      if (calendars.length > 0) {
        setIsInitializing(false);
        return;
      }
      
      // Only try to refresh if we have tokens in storage
      if (!tokensExist) {
        setIsInitializing(false);
        return;
      }
      
      // Try to refresh tokens and load calendars
      try {
        await refreshAccessToken();
        // Re-check connection status after refresh attempt
        const newTokensExist = isGoogleCalendarConnected();
        setHasTokensInStorage(newTokensExist);
        
        // Load calendars if we have valid tokens after refresh
        if (newTokensExist) {
          await loadGoogleCalendars();
        }
      } catch (error) {
        console.error('Failed to refresh Google Calendar tokens:', error);
        
        // Mark that auth error occurred to prevent retries
        if (error.isAuthError) {
          authErrorOccurredRef.current = true;
        }
        
        setHasTokensInStorage(false);
      }
      
      setIsInitializing(false);
    };

    initializeGoogleCalendar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - run only once on mount

  // Separate effect to handle when calendars become available
  useEffect(() => {
    if (calendars.length > 0 && isInitializing) {
      setIsInitializing(false);
    }
  }, [calendars.length, isInitializing]);

  // Add an effect to listen for storage changes when user logs in from GoogleAuth
  useEffect(() => {
    const handleStorageChange = () => {
      const tokensExist = isGoogleCalendarConnected();
      
      // If auth error occurred, don't retry - just update state
      if (authErrorOccurredRef.current && !tokensExist) {
        setHasTokensInStorage(false);
        setIsInitializing(false);
        return;
      }
      
      if (tokensExist && !hasTokensInStorage && !hasAttemptedLoadRef.current) {
        // User just logged in, reinitialize
        setHasTokensInStorage(tokensExist);
        hasAttemptedLoadRef.current = true;
        const initializeAfterLogin = async () => {
          setIsInitializing(true);
          try {
            // Access loadGoogleCalendars directly from the context
            await loadGoogleCalendars();
            // Success - reset the attempt flag
            hasAttemptedLoadRef.current = false;
          } catch (error) {
            console.error('Failed to load calendars after login:', error);
            
            // Mark auth error to prevent retries
            if (error.isAuthError) {
              authErrorOccurredRef.current = true;
            }
            setHasTokensInStorage(false);
            // Keep hasAttemptedLoadRef as true to prevent retries
          }
          setIsInitializing(false);
        };
        initializeAfterLogin();
      } else if (!tokensExist && hasTokensInStorage) {
        // User logged out or auth error - reset flags appropriately
        setHasTokensInStorage(false);
        setIsInitializing(false);
        // Only reset attempt flag if it wasn't an auth error
        if (!authErrorOccurredRef.current) {
          hasAttemptedLoadRef.current = false;
        }
      }
    };

    // Listen for storage events (when user logs in/out)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes (in case storage events don't fire)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [hasTokensInStorage, loadGoogleCalendars]);

  useEffect(() => {
    if (error && !authErrorOccurredRef.current) {
      authErrorOccurredRef.current = true;
      snackbarService.show({
        message: error,
        type: 'error',
        duration: SNACKBAR_DURATION,
      });
      setError(null);
    } else if (error) {
      // Clear the error without showing another snackbar
      setError(null);
    }
  }, [error, setError]);

  const handleCalendarToggle = useCallback((calendarId, checked) => {
    toggleCalendar(calendarId, checked);
  }, [toggleCalendar]);



  return (
    <>
      <GoogleSyncContainer>
        {isInitializing ? (
          // Show loading state while initializing
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading Google Calendar...</Typography>
          </Box>
        ) : !hasTokensInStorage ? (
          // Show GoogleAuth if user is not logged in at all
          <GoogleAuth />
        ) : isLoading || calendars.length === 0 ? (
          // Show loading state while fetching calendars
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={20} />
            <Typography variant="body2">Loading calendars...</Typography>
          </Box>
        ) : (
          // Show calendar selection if user is logged in and calendars are loaded
          <Autocomplete
            multiple
            limitTags={1}
            id="google-calendar-select"
            options={calendars}
            getOptionLabel={(option) => formatTitle(option.summary)}
            // ensure matching by id (prevents stale/duplicate selected items)
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            value={calendars.filter(cal => calendarData?.[cal.id]?.checked)}
            onChange={(event, newValue) => {
              // Check if we should allow this selection
              if (newValue.length > NUMBER_OF_CALENDAR_TO_SELECT) {
                snackbarService.show({
                  message: `You can only select up to ${NUMBER_OF_CALENDAR_TO_SELECT} calendars at a time.`,
                  type: 'warning',
                  duration: SNACKBAR_DURATION,
                });
                return;
              }

              // Get currently selected calendars
              const currentlySelected = calendars.filter(cal => calendarData?.[cal.id]?.checked);
              const currentlySelectedIds = currentlySelected.map(cal => cal.id);
              const newValueIds = newValue.map(cal => cal.id);

              // Find calendars to add and remove
              const calendarsToAdd = newValue.filter(cal => !currentlySelectedIds.includes(cal.id));
              const calendarsToRemove = currentlySelected.filter(cal => !newValueIds.includes(cal.id));

              // Apply changes
              calendarsToAdd.forEach(cal => handleCalendarToggle(cal.id, true));
              calendarsToRemove.forEach(cal => handleCalendarToggle(cal.id, false));
            }}
            getOptionDisabled={(option) => {
              const isSelected = calendarData?.[option.id]?.checked || false;
              const currentlySelected = calendars.filter(cal => calendarData?.[cal.id]?.checked);
              return !isSelected && currentlySelected.length >= NUMBER_OF_CALENDAR_TO_SELECT;
            }}
            disableCloseOnSelect
            renderOption={(props, option) => {
              const isSelected = calendarData?.[option.id]?.checked || false;
              const currentlySelected = calendars.filter(cal => calendarData?.[cal.id]?.checked);
              const isDisabled = !isSelected && currentlySelected.length >= NUMBER_OF_CALENDAR_TO_SELECT;
              
              // Remove the selected prop to avoid highlighting
              const { selected, ...restProps } = props;
              
              return (
                <Box
                  component="li"
                  {...restProps}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '8px 16px',
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: 'transparent !important',
                    '&:last-child': {
                      borderBottom: 'none'
                    },
                    // Remove all hover, focus, and selection highlighting
                    '&:hover': {
                      backgroundColor: 'transparent !important'
                    },
                    '&.Mui-focused': {
                      backgroundColor: 'transparent !important'
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'transparent !important'
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: 'transparent !important'
                    },
                    '&.Mui-focusVisible': {
                      backgroundColor: 'transparent !important'
                    }
                  }}
                  onClick={(e) => {
                    handleCalendarToggle(option.id, !isSelected);
                  }}
                >
                  <Checkbox
                    checked={isSelected}
                    disabled={isDisabled}
                    sx={{ 
                      marginRight: 1,
                      color: '#ccc',
                      '&.Mui-checked': {
                        color: '#0066cc'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCalendarToggle(option.id, !isSelected);
                    }}
                  />
                  <Typography variant="body2">
                    {formatTitle(option.summary)}
                  </Typography>
                </Box>
              );
            }}
            renderTags={(value, getTagProps) =>
              value.map((option) => (
                <OwcTypography key={option.id} style={{ marginLeft: '4px' }}>
                  {formatTitle(option.summary)}
                </OwcTypography>
              ))
            }
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Show calendar" 
                placeholder={`${calendars.some(cal => calendarData?.[cal.id]?.checked) ? '' : 'Select calendars'}`}
                variant="filled"
                size="small"
              />
            )}
            sx={{ width: '300px' }}
          />
        )}
      </GoogleSyncContainer>
    </>
  );
};

export default GoogleCalendarSync;
